// PortalShell — one chrome shared by every role's dashboard, modeled
// on credentik-app/v2's shell + MemberMD V2's PortalShell.
//
// Layout:
//   - Sidebar: 240px on lg+, slide-out drawer below (Menu button in topbar).
//   - Topbar: 56px sticky. Page title + actions on the left, language
//     switcher + user menu on the right.
//   - Brand block top-left of the sidebar with the gradient mark.
//   - Section-grouped nav items with active state.
//   - User menu (top-right): logged-in name + role, "Sign out".
//
// Callers pass `nav` (per-role NavSection[]) + `activeId` (current item)
// + `title` (page title). The shell renders chrome; children render the page.

import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, ChevronDown, ShoppingBag, UserCircle, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { auth as authApi } from '../../lib/api';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ViewOnPhoneButton } from './ViewOnPhoneButton';
import { cn } from '../../lib/cn';

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  badge?: number | string;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

interface Props {
  nav: NavSection[];
  activeId: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}

export function PortalShell({ nav, activeId, title, subtitle, actions, children }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-60 bg-white border-r border-slate-200 flex flex-col',
          'transform transition-transform lg:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <BrandBlock onClose={() => setDrawerOpen(false)} />
        <NavList nav={nav} activeId={activeId} onPick={() => setDrawerOpen(false)} />
        <UserMenu />
      </aside>

      {/* Main column */}
      <div className="lg:pl-60 flex flex-col min-h-screen">
        <TopBar
          title={title}
          subtitle={subtitle}
          actions={actions}
          onOpenDrawer={() => setDrawerOpen(true)}
        />
        {/* pb clears the fixed mobile bottom nav (hidden on lg+). */}
        <main className="flex-1 px-4 sm:px-6 py-6 pb-24 lg:pb-6">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile bottom tab bar (app-like nav, MemberMD/ShiftPulse style) */}
      <MobileBottomNav nav={nav} activeId={activeId} />
    </div>
  );
}

// ── Mobile bottom nav ───────────────────────────────────────────────────
// The primary handful of destinations as a fixed bottom tab bar on phones.
// The full nav stays available via the slide-out drawer. Derived from the
// first up-to-5 nav items so each role gets its most-used screens.

function MobileBottomNav({ nav, activeId }: { nav: NavSection[]; activeId: string }) {
  const items = nav.flatMap((s) => s.items).slice(0, 5);
  if (items.length === 0) return null;
  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 flex"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {items.map((it) => {
        const Active = it.id === activeId;
        return (
          <Link
            key={it.id}
            to={it.href}
            className={cn(
              'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors relative',
              Active ? 'text-brand-700' : 'text-slate-500',
            )}
          >
            <span className="relative">
              <it.icon className={cn('w-5 h-5', Active ? 'text-brand-600' : 'text-slate-400')} />
              {it.badge != null && it.badge !== 0 && it.badge !== '0' && (
                <span className="absolute -top-1 -right-2 min-w-[14px] h-[14px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold tabular-nums flex items-center justify-center">
                  {String(it.badge).length > 2 ? '99+' : it.badge}
                </span>
              )}
            </span>
            <span className="text-[10px] font-medium leading-none">{it.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

// ── Sidebar pieces ──────────────────────────────────────────────────────

function BrandBlock({ onClose }: { onClose: () => void }) {
  return (
    <div className="h-16 px-4 flex items-center justify-between border-b border-slate-100 shrink-0">
      <Link to="/" className="flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white">
          <ShoppingBag className="w-4 h-4" />
        </span>
        <span className="font-semibold tracking-tight text-[15px]">VoyeLakay</span>
      </Link>
      <button
        type="button"
        onClick={onClose}
        className="lg:hidden p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
        aria-label="Close menu"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function NavList({ nav, activeId, onPick }: { nav: NavSection[]; activeId: string; onPick: () => void }) {
  return (
    <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-5">
      {nav.map((section, i) => (
        <div key={i}>
          {section.title && (
            <p className="px-2.5 mb-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-400">{section.title}</p>
          )}
          <div className="space-y-0.5">
            {section.items.map((it) => {
              const Active = it.id === activeId;
              return (
                <Link
                  key={it.id}
                  to={it.href}
                  onClick={onPick}
                  className={cn(
                    'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] font-medium transition-colors',
                    Active
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                  )}
                >
                  <it.icon className={cn('w-4 h-4', Active ? 'text-brand-600' : 'text-slate-400')} />
                  <span className="flex-1 truncate">{it.label}</span>
                  {it.badge != null && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-600 text-white text-[10px] font-semibold">
                      {it.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

// Maps the auth'd user's role to the URL of their settings page so
// the user-menu "Profile & settings" link routes correctly per role.
function settingsPathFor(role: string | undefined): string {
  switch (role) {
    case 'admin':
    case 'super_admin': return '/admin/settings';
    case 'merchant':    return '/merchant/settings';
    case 'recipient':   return '/recipient/settings';
    case 'customer':
    default:            return '/customer/settings';
  }
}

function UserMenu() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const signOut = async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    await refresh();
    navigate('/login', { replace: true });
  };

  const displayName = user?.customer_profile && (user.customer_profile as { full_name?: string }).full_name
    ? (user.customer_profile as { full_name: string }).full_name
    : user?.merchant_profile && (user.merchant_profile as { business_name?: string }).business_name
    ? (user.merchant_profile as { business_name: string }).business_name
    : user?.admin_profile && (user.admin_profile as { display_name?: string }).display_name
    ? (user.admin_profile as { display_name: string }).display_name
    : user?.recipient_profile && (user.recipient_profile as { full_name?: string }).full_name
    ? (user.recipient_profile as { full_name: string }).full_name
    : user?.email ?? 'Account';

  return (
    <div className="border-t border-slate-100 p-3 relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-slate-50"
      >
        <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-slate-900 text-white text-[12px] font-semibold shrink-0">
          {displayName.charAt(0).toUpperCase()}
        </span>
        <span className="flex-1 min-w-0 text-left">
          <span className="block text-[12.5px] font-semibold text-slate-900 truncate">{displayName}</span>
          <span className="block text-[11px] text-slate-500 capitalize">{user?.role}</span>
        </span>
        <ChevronDown className={cn('w-3.5 h-3.5 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50">
          <Link
            to={settingsPathFor(user?.role)}
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50"
          >
            <UserCircle className="w-3.5 h-3.5 text-slate-400" /> Profile & settings
          </Link>
          <button
            type="button"
            onClick={signOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50"
          >
            <LogOut className="w-3.5 h-3.5 text-slate-400" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ── Top bar ─────────────────────────────────────────────────────────────

function TopBar({ title, subtitle, actions, onOpenDrawer }: { title: string; subtitle?: string; actions?: ReactNode; onOpenDrawer: () => void }) {
  const { t } = useTranslation();
  return (
    <header className="h-16 sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-100 flex items-center px-4 sm:px-6 gap-3">
      <button
        type="button"
        onClick={onOpenDrawer}
        className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-600"
        aria-label="Open menu"
      >
        <Menu className="w-4.5 h-4.5" />
      </button>
      <div className="min-w-0 flex-1">
        <h1 className="text-[16px] sm:text-[18px] font-semibold tracking-tight text-slate-900 truncate">{title}</h1>
        {subtitle && <p className="text-[12px] text-slate-500 truncate -mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {actions}
        <ViewOnPhoneButton />
        <LanguageSwitcher />
      </div>
      <span className="sr-only">{t('nav.signIn')}</span>
    </header>
  );
}
